import "../src/styles.css";
import "./style.css";

import { NARWEE_FIXTURE } from "../src/fixtures/narwee.ts";
import { mountRoofVisualizer } from "../src/roofviz/mount.ts";
import type { ProfileId } from "../src/roofviz/materials.ts";
import type { CardinalView } from "../src/roofviz/scene.ts";

const visualizerRoot = document.querySelector<HTMLElement>("#runs-visualizer");
const visualizerShell = document.querySelector<HTMLElement>(".visualizer-shell");

if (!visualizerRoot || !visualizerShell) throw new Error("Runs visualizer root is missing.");

const referencePacket = {
  ...NARWEE_FIXTURE.packet,
  property: {
    ...NARWEE_FIXTURE.packet.property,
    label: "Reference roof",
  },
};

const query = new URLSearchParams(window.location.search);
const qaPosterMode = import.meta.env.DEV && query.has("qaPoster");
const qaReducedMotion = import.meta.env.DEV && query.has("qaReducedMotion");
if (qaReducedMotion) document.documentElement.classList.add("qa-reduced-motion");

const visualizer = qaPosterMode ? null : mountRoofVisualizer({
  root: visualizerRoot,
  packet: referencePacket,
  presentation: {
    shell: "embedded",
    footprints: NARWEE_FIXTURE.footprints,
    assumedHeights: NARWEE_FIXTURE.assumedHeights,
    homeownerNote: "Visual guide only.",
    autoRotate: !qaReducedMotion,
    autoRotateSpeed: 0.42,
    startInRoof: true,
    pixelRatioCap: 2.5,
    toneMappingExposure: 1,
    framingMarginDesktop: 1.02,
    framingMarginPortrait: 1.12,
  },
  initialSession: {
    appearance: { profile: "true_oak_corrugate", colour: "ironsand" },
    gutter: "quarter_round_150",
    fascia: "multifit_185",
    view: "perspective",
  },
  onStatusChange(status) {
    visualizerRoot.dataset.state = status.state;
    if (status.state === "ready") visualizerRoot.classList.add("is-ready");
  },
});

if (qaPosterMode) {
  visualizerRoot.dataset.state = "poster";
  visualizerRoot.setAttribute("aria-label", "Static poster of the same reference roof");
}

document.querySelectorAll<HTMLButtonElement>(".material-choice").forEach((button) => {
  button.addEventListener("click", () => {
    const profile = button.dataset.profile as ProfileId | undefined;
    const colour = button.dataset.colour;
    if (!profile || !colour) return;
    visualizer?.setAppearance({ profile, colour });
    document.querySelectorAll<HTMLButtonElement>(".material-choice").forEach((candidate) => {
      const active = candidate === button;
      candidate.classList.toggle("is-active", active);
      candidate.setAttribute("aria-pressed", String(active));
    });
  });
});

const cardinalViews: CardinalView[] = ["north", "east", "south", "west"];
let cardinalIndex = 0;
visualizerShell.dataset.view = "perspective";

visualizerShell.addEventListener("keydown", (event) => {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  cardinalIndex = (cardinalIndex + (event.key === "ArrowRight" ? 1 : -1) + cardinalViews.length) % cardinalViews.length;
  const view = cardinalViews[cardinalIndex];
  visualizer?.setView(view);
  visualizerShell.dataset.view = view;
});

function roofTraceMarkup(): string {
  const rings = referencePacket.planes.map((plane) => plane.ring);
  const points = rings.flat();
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const width = Math.max(...xs) - minX;
  const height = Math.max(...ys) - minY;
  const pad = Math.max(width, height) * 0.04;
  const paths = rings.map((ring) => {
    const commands = ring.map(([x, y], index) => `${index === 0 ? "M" : "L"} ${(x - minX + pad).toFixed(3)} ${(y - minY + pad).toFixed(3)}`);
    return `<path d="${commands.join(" ")} Z"></path>`;
  }).join("");
  return `<svg class="roof-trace-svg" viewBox="0 0 ${(width + pad * 2).toFixed(3)} ${(height + pad * 2).toFixed(3)}" role="presentation" focusable="false">${paths}</svg>`;
}

const traceMarkup = roofTraceMarkup();
document.querySelectorAll<HTMLElement>("[data-roof-trace]").forEach((element) => {
  element.innerHTML = traceMarkup;
});

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches || qaReducedMotion;
const revealBlocks = [...document.querySelectorAll<HTMLElement>(".reveal-block")];

if (reducedMotion || !("IntersectionObserver" in window)) {
  revealBlocks.forEach((block) => block.classList.add("is-visible"));
} else {
  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.16, rootMargin: "0px 0px -8%" });
  revealBlocks.forEach((block) => revealObserver.observe(block));
}

window.addEventListener("beforeunload", () => visualizer?.destroy(), { once: true });

type PreviewPerformance = { lcpMs: number | null; cls: number; maxInteractionMs: number };
const previewPerformance: PreviewPerformance = { lcpMs: null, cls: 0, maxInteractionMs: 0 };
Object.defineProperty(window, "__runsPreviewPerformance", { value: previewPerformance, configurable: true });
document.documentElement.dataset.cls = "0";
document.documentElement.dataset.maxInteractionMs = "0";

if ("PerformanceObserver" in window) {
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const latest = entries[entries.length - 1];
      if (latest) {
        previewPerformance.lcpMs = latest.startTime;
        document.documentElement.dataset.lcpMs = latest.startTime.toFixed(1);
      }
    }).observe({ type: "largest-contentful-paint", buffered: true });
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
        if (!shift.hadRecentInput) {
          previewPerformance.cls += shift.value ?? 0;
          document.documentElement.dataset.cls = previewPerformance.cls.toFixed(4);
        }
      });
    }).observe({ type: "layout-shift", buffered: true });
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        previewPerformance.maxInteractionMs = Math.max(previewPerformance.maxInteractionMs, entry.duration);
        document.documentElement.dataset.maxInteractionMs = previewPerformance.maxInteractionMs.toFixed(1);
      });
    }).observe({ type: "event", buffered: true, durationThreshold: 16 } as PerformanceObserverInit);
  } catch {
    // Older browsers keep the experience intact; the QA packet records unavailable metrics.
  }
}
